import LoginLayout from '@/layout/LoginLayout';
import { useConfigStore } from '@/store/configStore';
import { useUserStore } from '@/store/userStore';
import { trpc, trpcClient } from '@/trpcClient';
import { applyThemeColor, applyFavicon } from '@/utils/utils';
import { preloadImages } from '@/utils/imagePreloadCache';
import shelflixFavicon from '@/assets/shelflix-favicon.ico';
import { getValidAuthToken, decodeToken } from '@/utils/auth';
import { AuthenticationLoginInputType, checkAuthenticationLoginInput } from '@shelflix/schemas/authentication/login';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useMutation } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLoginFormStore } from './store';

type DecodedToken = {
  scope?: string[];
  name: string;
  email: string;
  tenantId: number;
  isMaster: boolean;
  tenantSlug?: string;
};

export default function Login() {
  const { formData, resetForm } = useLoginFormStore();
  const loginMutation = useMutation(trpc.authentication.login.mutationOptions());
  const navigate = useNavigate();
  const location = useLocation();
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);

  const setUser = useUserStore((state) => state.setUser);

  useEffect(() => {
    // Check for valid (non-expired) token
    const validToken = getValidAuthToken();

    if (validToken) {
      const decoded = decodeToken(validToken);

      if (decoded) {
        setUser({
          email: decoded.email ?? '',
          name: decoded.name ?? '',
          tenantId: decoded.tenantId ?? 0,
          isMaster: decoded.isMaster ?? false,
        });

        // Navigate directly to /pos instead of / to avoid redirect loop
        navigate('/pos', { replace: true, state: { toastUnauthorized: location.state?.toastUnauthorized } });
        return;
      }
    }

    // No valid token - show login form
    setLoading(false);
  }, [navigate, location.state?.toastUnauthorized, setUser]);

  // Auto-focus email field on desktop (not on mobile to avoid keyboard popup)
  useEffect(() => {
    if (!loading && emailInputRef.current) {
      // Only auto-focus on desktop (viewport width > 768px)
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      if (isDesktop) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          emailInputRef.current?.focus();
        }, 100);
      }
    }
  }, [loading]);

  const form = useForm<AuthenticationLoginInputType>({
    defaultValues: {
      ...formData,
    },
    mode: 'all',
  });

  const onSubmit = (data: AuthenticationLoginInputType) => {
    if (loginMutation.isPending) return;
    const payload = {
      ...data,
    };

    loginMutation.mutate(payload, {
      onSuccess: async (response) => {
        localStorage.setItem('authToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        resetForm();

        const decoded = jwtDecode<DecodedToken>(response.accessToken);

        setUser({
          email: decoded.email,
          name: decoded.name,
          tenantId: decoded.tenantId,
          isMaster: decoded.isMaster ?? false,
        });

        // Fetch and apply the correct tenant theme before navigating
        // This avoids a full page reload while ensuring correct branding
        const configResponse = await trpcClient.applicationConfig.get.query({
          tenantSlug: response.tenantSlug,
        });
        const config = configResponse.config;

        // Preload logos to avoid flicker
        if (config?.logoUrl || config?.logoDarkUrl) {
          await Promise.race([
            preloadImages([config?.logoUrl, config?.logoDarkUrl]),
            new Promise((resolve) => setTimeout(resolve, 500)),
          ]);
        }

        // Apply theme colors
        if (config?.themeColor) {
          applyThemeColor(config.themeColor);
        }

        // Apply favicon
        if (config?.faviconUrl) {
          applyFavicon(config.faviconUrl);
        } else {
          applyFavicon(shelflixFavicon);
        }

        // Update page title with tenant name
        if (config?.name) {
          document.title = config.name;
        }

        // Update the config store
        const { setConfig } = useConfigStore.getState();
        setConfig(config);

        // Navigate without page reload
        navigate('/pos', { replace: true, state: { toastUnauthorized: false } });
      },
      onError: (error) => {
        toast.error(`${error.message}`);
      },
    });
  };

  return loading ? null : (
    <LoginLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="email"
            rules={{ validate: (value) => checkAuthenticationLoginInput('email', value) }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Login</FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <span className="material-symbols-outlined pointer-events-none absolute left-3 text-base text-gray-400">
                      person
                    </span>
                    <Input
                      type="email"
                      id="email"
                      placeholder="E-mail"
                      className="pl-9"
                      ref={emailInputRef}
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            rules={{ validate: (value) => checkAuthenticationLoginInput('password', value) }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Senha</FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <span className="material-symbols-outlined pointer-events-none absolute left-3 text-base text-gray-400">
                      key_vertical
                    </span>
                    <Input type="password" id="password" placeholder="Digite sua senha" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <a href="/verification-receive" className="text-sm text-[var(--primary)] hover:underline">
            Esqueci minha senha
          </a>

          <Button type="submit" className="w-full bg-[var(--primary)]" loading={loginMutation.isPending}>
            {loginMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Carregando...</span>
              </div>
            ) : (
              <>
                <Icon name="login" className="mr-2" />
                Login
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--secondary)]"
            onClick={() => navigate('/verification-receive?firstAccess')}
          >
            Primeiro acesso
          </Button>
        </form>
      </Form>
    </LoginLayout>
  );
}
